// ── Session types ──────────────────────────────────────────────

export type SessionStatus =
  | 'queued'
  | 'initialising'
  | 'ringing'
  | 'in_progress'
  | 'completed'
  | 'failed'
  | 'callback_scheduled'
  | 'voicemail'
  | 'requested_email'
  | 'no_answer'
  | 'ignored'
  | 'unknown';

export type FlowType = 'schedule' | 'reschedule' | 'no_show';

export type SessionDirection = 'inbound' | 'outbound';

export interface TranscriptEntry {
  speaker: 'agent' | 'candidate';
  text: string;
}

export interface Session {
  id: number;
  parent_voice_session_id: number | null;
  status: SessionStatus;
  direction: SessionDirection;
  flow_type: FlowType | null;
  created_at: string;
  updated_at: string;
  ended_at: string | null;
  transcript: TranscriptEntry[] | null;
  recording_url: string | null;
  recording_url_expires_at: string | null;
  recording_duration_seconds: number | null;
  call_duration_seconds: number | null;
  no_show_summary: string | null;
  callback_scheduled_at: string | null;
}

export interface SessionsResponse {
  sessions: Session[];
}

// ── Active session check ───────────────────────────────────────

export interface ActiveSessionStatusParams {
  subject_type: 'ats_application';
  subject_id: number;
  ats_job_stage_id: number;
}

export interface ActiveSessionStatusResponse {
  has_active_session: boolean;
  active_session: {
    id: number;
    status: string;
    scheduled_callback_at: string | null;
  } | null;
}

// ── Create session (POST /coordinator_sessions) ─────────────────

export interface CreateCoordinatorSessionPayload {
  subject_type: 'ats_application' | 'ats_interview';
  /** ATS application id or interview id (for reschedule) */
  subject_id: number;
  ats_job_stage_id: number;
  ats_job_stage_action_id: number | null;
  interview_name: string;
  interview_kind: 'one_on_one' | 'panel';
  duration_minutes: number;
  location: 'zoom' | 'google_meet' | 'teams' | 'phone_interview' | 'onsite';
  /** Required when location is onsite */
  ats_office_id?: number;
  participant_ids: number[];
  session_type: 'schedule' | 'reschedule';
  /** ISO 8601 datetime (e.g. 2026-02-24T09:55:00Z). When to run the coordinator call. */
  scheduled_at: string | null;
  /** Optional; backend may accept for context */
  coordinator_template_id?: number;
  note_taker_enabled?: boolean;
  calendar_visibility?: 'default' | 'public' | 'private';
  meeting_room_emails?: string[];
  organizer_notes?: string;
  /** Additional context (HTML) for the coordinator agent */
  additional_context?: string;
  /** Reason for rescheduling (reschedule flow); sent to coordinator/candidate */
  reschedule_reason?: string;
  source_phone_number_id?: number;
  /** Ad-hoc: scorecard and templates */
  scorecard_config?: AdhocScorecardConfig;
  scorecard_feedback_description?: string;
  scorecard_rating_description?: string;
  candidate_template_id?: number;
  interviewer_template_id?: number;
  candidate_description?: string;
  interviewer_description?: string;
  fill_with_ai_enabled?: boolean;
}

/** No-show session (POST /coordinator_sessions): check on candidate flow */
export interface CreateNoShowCoordinatorSessionPayload {
  subject_type: 'ats_interview';
  subject_id: number;
  ats_application_id: number;
  session_type: 'no_show';
}

/** Ad-hoc session: scorecard config for POST /coordinator_sessions */
export interface AdhocScorecardConfig {
  is_feedback_mandatory?: boolean;
  questions?: {
    type: string;
    question: string;
    mandatory?: boolean;
    description?: string;
  }[];
}

/** Create session success: { data: { id, cache_key_id, status, scheduled_at }, errors: [] } */
export interface CreateCoordinatorSessionResponse {
  data: {
    id: number;
    cache_key_id: string;
    status: string;
    scheduled_at: string;
  };
  errors: string[];
  /** HTTP status code — present on error responses */
  status?: number;
}

/** GET /coordinator_sessions/:id – session for polling (ATS API) */
export interface CoordinatorSessionResponse {
  id: number;
  status: string;
  session_type: string;
  ats_application_id: number;
  ats_interview_id: number | null;
  created_at: string;
  updated_at: string;
  no_show_status: string | null;
  scheduled_at: string | null;
  scheduled_callback_at: string | null;
  retry_at?: string | null;
  retry_reason?: string | null;
  /** Optional; UI falls back to generic retry copy when omitted. */
  retry_attempt?: number | null;
  max_retry_attempts?: number | null;
  max_retries?: number | null;
  /** Present when session has config (e.g. completed or has interview_config); used to pre-fill edit/reschedule form */
  interview_config?: {
    name: string;
    kind: string;
    duration_minutes: number;
    location: string;
    participant_ids: number[];
    note_taker_enabled?: boolean;
    calendar_visibility?: 'default' | 'public' | 'private';
    ats_office_id?: number | null;
  };
  coordinator_templates?: { id: number }[];
}

/**
 * Session summary as returned inside GET ats_applications/:id (ai_coordinator.active_sessions / last_sessions).
 * Can be minimal { id, status, ats_job_stage_id, ats_job_stage_action_id } or full session payload.
 */
export interface ApplicationCoordinatorSessionSummary {
  id: number;
  status: string;
  ats_job_stage_id: number;
  ats_job_stage_action_id: number | null;
  session_type?: string;
  ats_application_id?: number;
  ats_interview_id?: number | null;
  created_at?: string;
  updated_at?: string;
  no_show_status?: string | null;
  scheduled_at?: string | null;
  scheduled_callback_at?: string | null;
  retry_at?: string | null;
  retry_reason?: string | null;
  retry_attempt?: number | null;
  max_retry_attempts?: number | null;
  max_retries?: number | null;
  interview_config?: CoordinatorSessionResponse['interview_config'];
  coordinator_templates?: { id: number }[];
}

/** Wrapper for GET /coordinator_sessions/:id response */
export interface CoordinatorSessionByIdResponse {
  data: CoordinatorSessionResponse;
  errors: unknown[];
}

// ── Bulk validate (GET /coordinator_sessions/validate_bulk) ──────

export interface ValidateBulkParams {
  ats_job_stage_id: number;
  ats_application_ids: number[];
}

export interface ValidateBulkApplicationItem {
  id: number;
  contact: {
    id: number;
    first_name: string;
    last_name: string;
    image_url: string | null;
    /** E.164-style string e.g. "+919952195845" (country code + number) */
    phone_number?: string | null;
    /** IANA timezone key e.g. "America/New_York" */
    time_zone?: string | null;
  };
}

export interface ValidateBulkInvalidOrWarningGroup {
  reason: string;
  ats_applications: ValidateBulkApplicationItem[];
}

export interface ValidateBulkResponse {
  valid_applications: ValidateBulkApplicationItem[];
  invalid_applications: ValidateBulkInvalidOrWarningGroup[];
  warning_applications: ValidateBulkInvalidOrWarningGroup[];
}

// ── Bulk create (POST /coordinator_sessions/bulk) ─────────────────

export interface CreateBulkCoordinatorSessionPayload {
  ats_application_ids: number[];
  ats_job_stage_id: number;
  ats_job_stage_action_id: number;
  interview_kind: 'one_on_one' | 'panel';
  duration_minutes: number;
  participant_ids: number[];
  is_bulk_activity: true;
  location: 'zoom' | 'google_meet' | 'teams' | 'phone_interview' | 'onsite';
  ats_office_id?: number;
}

export interface CreateBulkCoordinatorSessionResponse {
  id: number;
}

// ── Coordinator templates ──────────────────────────────────────

export interface CoordinatorTemplate {
  id: number;
  name: string;
  body: string;
  status: 'draft' | 'published';
  created_at: string;
  updated_at: string;
}

export interface CoordinatorTemplatesResponse {
  data: CoordinatorTemplate[];
  errors: string[];
}

// ── Interview plan (from existing ScheduleAPI) ─────────────────

export interface InterviewPlanParticipant {
  id: number;
  name: string;
  email?: string;
  avatar_url?: string;
  avatar?: string;
}

export interface InterviewPlan {
  id?: number;
  name?: string;
  substituted_name?: string;
  kind?: 'one_on_one' | 'panel';
  interview_kind?: 'one_on_one' | 'panel';
  participants?: InterviewPlanParticipant[];
  interviewers?: InterviewPlanParticipant[];
  duration?: number;
  location?: string;
  note_taker_enabled?: boolean;
}

// ── Account-level coordinator config (GET/PATCH /coordinator_agent_config) ─

export interface CoordinatorAgentConfig {
  id: number;
  enabled: boolean;
  agent_name: string;
  voice_id: string;
  inbound_rescheduling_enabled: boolean;
  interview_reminders_enabled: boolean;
  no_show_follow_up_enabled: boolean;
  outbound_scheduling_enabled: boolean;
}

export interface CoordinatorAgentConfigResponse {
  data: CoordinatorAgentConfig;
  errors: string[];
}

export interface CoordinatorAgentConfigPatchPayload {
  enabled?: boolean;
  agent_name?: string;
  voice_id?: string;
  inbound_rescheduling_enabled?: boolean;
  interview_reminders_enabled?: boolean;
  no_show_follow_up_enabled?: boolean;
  outbound_scheduling_enabled?: boolean;
}

// ── Job-level coordinator config (GET/POST/PATCH ats_jobs/:id/coordinator_job_config) ─

export interface CoordinatorJobConfigTemplate {
  id: number;
  name: string;
  body: string;
}

export interface CoordinatorJobConfigAdditionalContext {
  id: number;
  body: string;
}

export interface CoordinatorJobConfig {
  id: number;
  voice_id: string;
  source_phone_number_id: number | null;
  enabled: boolean;
  coordinator_templates: CoordinatorJobConfigTemplate[];
  additional_context: CoordinatorJobConfigAdditionalContext | null;
}

export interface CoordinatorJobConfigResponse {
  data: CoordinatorJobConfig;
  errors: string[];
}

/** Response shape of GET ats_jobs/:id/coordinator_job_config/new (defaults for first-time). */
export interface CoordinatorJobConfigNewData {
  voice_id: string | null;
  outbound_scheduling_enabled: boolean;
  inbound_rescheduling_enabled: boolean;
  interview_reminders_enabled: boolean;
  no_show_follow_up_enabled: boolean;
}

export interface CoordinatorJobConfigNewResponse {
  data: CoordinatorJobConfigNewData;
  errors: string[];
}

export interface CoordinatorJobConfigCreatePayload {
  coordinator_job_config: {
    voice_id: string;
    enabled: boolean;
    coordinator_template_ids: number[];
    source_phone_number_id?: number | null;
    additional_context?: { body: string };
  };
}

export interface CoordinatorJobConfigPatchPayload {
  coordinator_job_config: Partial<{
    voice_id: string;
    enabled: boolean;
    coordinator_template_ids: number[];
    source_phone_number_id: number | null;
    additional_context: { body: string };
  }>;
}

// ── Error codes ────────────────────────────────────────────────

export const COORDINATOR_ERROR_MESSAGES: Record<string, string> = {
  err_phone_number_missing: 'Missing phone number',
  err_country_missing: 'Candidate country is missing, please add and try again',
  err_phone_country_code_missing: 'Invalid phone format',
  err_active_session_exists: 'Session already in progress',
  err_coordinator_agent_not_enabled:
    'AI coordinator is not enabled for this job or organization.',
  err_coordinator_job_config_not_found:
    'Coordinator job config not found. Create one first.',
  err_scheduled_at_must_be_future: 'Scheduled at must be in the future',
  warn_timezone_unresolvable:
    'Timezone could not be resolved; calls may be queued for earliest time.',
};
